import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminUser } from '../../lib/admin';

type NavbarProps = {
    heroMode?: boolean;
    resumePath?: string | null;
    trackerUnlocked?: boolean;
    confidence?: number;
};

const routeLabel = (path: string | null | undefined) => {
    switch (path) {
        case '/crucible':
        case '/eisenboard':
            return 'Resume Eisenboard';
        case '/tracker':
            return 'Resume Tracker';
        case '/input-loop':
            return 'Resume Input Loop';
        default:
            return 'Resume Session';
    }
};

const Navbar: React.FC<NavbarProps> = ({
    heroMode = false,
    resumePath = '/crucible',
    trackerUnlocked = false,
    confidence = 0,
}) => {
    const location = useLocation();
    const { user } = useAuth();
    const showAdmin = isAdminUser(user);

    const isActive = (path: string) => location.pathname === path;

    if (heroMode) {
        const safeResumePath = resumePath || '/crucible';

        return (
            <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 py-4 bg-black/80 backdrop-blur-md border-b border-white/10">
                <Link to="/" className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-400 hover:opacity-80 transition-opacity uppercase">
                    Crucible
                </Link>

                <div className="flex items-center gap-2">
                    <Link
                        to={safeResumePath}
                        className="px-4 py-2 rounded-lg text-sm font-bold bg-white/10 text-slate-100 hover:bg-white/15 border border-white/10 transition-all"
                    >
                        {routeLabel(safeResumePath)}
                    </Link>
                    {trackerUnlocked && (
                        <Link
                            to="/tracker"
                            className="px-4 py-2 rounded-lg text-sm font-bold bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-400/40 transition-all"
                            title="Tracker unlocked at 90% confidence"
                        >
                            Open Tracker ({confidence}%)
                        </Link>
                    )}
                </div>
            </nav>
        );
    }

    return (
        <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 py-4 bg-black/80 backdrop-blur-md border-b border-white/10">
            <div className="flex items-center gap-8">
                <Link to="/" className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-400 hover:opacity-80 transition-opacity uppercase">
                    Crucible
                </Link>

                <div className="flex gap-1 bg-white/5 rounded-lg p-1 border border-white/10">
                    {[
                        { path: '/crucible', label: 'Eisenboard' },
                        { path: '/tracker', label: 'Tracker' },
                    ].map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${isActive(link.path)
                                ? 'bg-slate-700 text-white shadow-lg'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>
            </div>

            <div className="flex gap-6 text-xs font-mono text-slate-500">
                <Link to="/settings" className="hover:text-slate-200 transition-colors">Settings</Link>
                {showAdmin && (
                    <Link to="/admin" className="hover:text-red-400 transition-colors">Admin</Link>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
